import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DescriptionIcon from '@mui/icons-material/Description';
import RefreshIcon from '@mui/icons-material/Refresh';
import api from '../../services/api';
import { getRxStatusColor, getRxStatusLabel, INVALID_RX_STATUSES } from '../../constants/rxStatus';
import { formatDateForDisplay } from '../../utils/formatters';
import { panelNeutral } from '../../theme/gridSurfaces';

/**
 * GP-1038 — per-Rx fill history, one click from the prescription line.
 *
 * Kroll's Rx "detail" screen shows every fill in a prescription's series (how
 * many fills, when, and which ones were cancelled). PHOX already had the same
 * data, but only via Patient 360 → History → All RXs → row → Fill History tab —
 * four clicks deep, so nobody used it during Clinical Verification. This is the
 * shared surface behind the one-click entry point on the Rx navigator.
 *
 * Source: GET /api/rx/:pharmacyId/rx-detail/:rxNum → data.rxDetails — every
 * Pharmacy.dbo.Rx row sharing the OrigRxNum, newest fill first.
 *
 * Kroll stores NO cancel-reason text on Rx; the cancellation facts it does hold
 * are the status (Cancelled / Deleted-Void / Exp. Svc) and CancelDate, so those
 * are what we show. Nothing here is inferred or defaulted.
 */

/** One Kroll fill row (Pharmacy.dbo.Rx) from the rx-detail response. */
export interface RxFillHistoryEntry {
  RxNum: number;
  FillDate: string | null;
  CancelDate: string | null;
  Status: number;
  DispQty: number | null;
  DaysSupply: number | null;
  Cost: number | null;
  Fee: number | null;
  /** Kroll's gap (days) between this fill and the previous dispensed one. */
  Adherence: number | null;
  Init: string | null;
}

export interface RxFillHistorySummary {
  /** Rows in the series (every fill Kroll holds for this OrigRxNum). */
  total: number;
  /** Fills still standing — status not in INVALID_RX_STATUSES. */
  dispensed: number;
  /** Cancelled / deleted / expired-service fills. */
  cancelled: number;
  /** Earliest / latest fill date across the standing fills (YYYY-MM-DD). */
  firstFillDate: string | null;
  lastFillDate: string | null;
}

/** Date part of a Kroll timestamp; YYYY-MM-DD strings sort correctly as-is. */
const dateOnly = (value: string | null | undefined): string | null => {
  if (!value) return null;
  const part = String(value).split('T')[0];
  return part || null;
};

/**
 * Roll a fill series up into the counts a pharmacist reads first: how many
 * fills, how many were cancelled, and the span they cover.
 */
export const summarizeFillHistory = (
  fills: RxFillHistoryEntry[] | null | undefined,
): RxFillHistorySummary => {
  const rows = fills || [];
  let cancelled = 0;
  let firstFillDate: string | null = null;
  let lastFillDate: string | null = null;

  for (const fill of rows) {
    if (INVALID_RX_STATUSES.includes(fill.Status)) {
      cancelled += 1;
      continue;
    }
    const filled = dateOnly(fill.FillDate);
    if (!filled) continue;
    if (!firstFillDate || filled < firstFillDate) firstFillDate = filled;
    if (!lastFillDate || filled > lastFillDate) lastFillDate = filled;
  }

  return {
    total: rows.length,
    dispensed: rows.length - cancelled,
    cancelled,
    firstFillDate,
    lastFillDate,
  };
};

const formatDate = (value: string | null | undefined): string => {
  const part = dateOnly(value);
  return part ? formatDateForDisplay(part) : '—';
};

const formatMoney = (value: number | null | undefined): string =>
  `$${(Number(value) || 0).toFixed(2)}`;

interface RxFillHistoryTableProps {
  fills: RxFillHistoryEntry[];
  /** Rx being viewed — its row is highlighted in the series. */
  highlightRxNum?: number | string | null;
  /**
   * GP-1368 — open a fill's own hardcopy. When supplied the table grows a
   * Hardcopy column and every row becomes clickable, so the ~30 historical
   * fills of a medication are reachable instead of only the newest Rx. Left
   * out (Clinical Verification's one-click dialog) the table is read-only, as
   * it has been since GP-1038.
   */
  onOpenHardcopy?: (rxNum: number) => void;
}

/**
 * The fill-history table itself. Shared by the one-click dialog below and by
 * Patient 360 → All Prescriptions → Fill History so both stay identical.
 */
export const RxFillHistoryTable: React.FC<RxFillHistoryTableProps> = ({
  fills,
  highlightRxNum,
  onOpenHardcopy,
}) => {
  const theme = useTheme();

  if (fills.length === 0) {
    return <Typography color="text.secondary">No fills recorded for this prescription</Typography>;
  }

  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ bgcolor: panelNeutral }}>
            <TableCell sx={{ fontWeight: 700 }}>RxNum</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Fill Date</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Cancel Date</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Qty</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Days</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Cost</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Fee</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Adherence</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Init</TableCell>
            {onOpenHardcopy && <TableCell sx={{ fontWeight: 700 }}>Hardcopy</TableCell>}
          </TableRow>
        </TableHead>
        <TableBody>
          {fills.map((fill) => {
            const isCurrent =
              highlightRxNum != null && String(fill.RxNum) === String(highlightRxNum);
            return (
              <TableRow
                key={fill.RxNum}
                hover={!!onOpenHardcopy}
                data-testid={`rx-fill-history-row-${fill.RxNum}`}
                onClick={onOpenHardcopy ? () => onOpenHardcopy(fill.RxNum) : undefined}
                sx={{
                  bgcolor: isCurrent ? alpha(theme.palette.primary.main, 0.1) : 'inherit',
                  ...(onOpenHardcopy
                    ? {
                        cursor: 'pointer',
                        '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.08) },
                      }
                    : {}),
                }}
              >
                <TableCell>
                  <Typography fontFamily="monospace" fontWeight={isCurrent ? 700 : 400}>
                    {fill.RxNum}
                  </Typography>
                </TableCell>
                <TableCell>{formatDate(fill.FillDate)}</TableCell>
                <TableCell>
                  <Chip
                    label={getRxStatusLabel(fill.Status)}
                    size="small"
                    color={getRxStatusColor(fill.Status)}
                  />
                </TableCell>
                <TableCell>{formatDate(fill.CancelDate)}</TableCell>
                <TableCell>{fill.DispQty}</TableCell>
                <TableCell>{fill.DaysSupply}</TableCell>
                <TableCell>{formatMoney(fill.Cost)}</TableCell>
                <TableCell>{formatMoney(fill.Fee)}</TableCell>
                <TableCell>
                  {fill.Adherence !== null && fill.Adherence !== undefined ? (
                    <Chip
                      label={fill.Adherence > 0 ? `+${fill.Adherence}` : fill.Adherence}
                      size="small"
                      color={fill.Adherence < -7 ? 'error' : fill.Adherence > 7 ? 'warning' : 'success'}
                      variant="outlined"
                    />
                  ) : '—'}
                </TableCell>
                <TableCell>{fill.Init || '—'}</TableCell>
                {onOpenHardcopy && (
                  <TableCell>
                    <IconButton
                      size="small"
                      color="primary"
                      title={`View hardcopy for Rx #${fill.RxNum}`}
                      aria-label={`View hardcopy for Rx ${fill.RxNum}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        onOpenHardcopy(fill.RxNum);
                      }}
                    >
                      <DescriptionIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                )}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export interface RxFillHistoryDialogProps {
  open: boolean;
  onClose: () => void;
  /** Rx being verified — the whole OrigRxNum series is fetched around it. */
  rxNumber?: string | number | null;
  pharmacyId?: string | null;
  /** Shown in the title so the pharmacist sees which drug they opened. */
  medicationName?: string;
}

/**
 * One-click per-Rx fill history. Fetches on open (and on Rx change) so the
 * pharmacist never has to leave Clinical Verification to answer "how many
 * times has this been filled, and were any of them cancelled?".
 */
export const RxFillHistoryDialog: React.FC<RxFillHistoryDialogProps> = ({
  open,
  onClose,
  rxNumber,
  pharmacyId,
  medicationName,
}) => {
  const [fills, setFills] = useState<RxFillHistoryEntry[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchFillHistory = useCallback(async () => {
    if (!rxNumber || !pharmacyId) {
      // Never fetch (or render) against a guessed pharmacy/Rx — say what's missing.
      setError(`Cannot load fill history: missing ${!rxNumber ? 'Rx number' : 'pharmacy'}`);
      setFills(null);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await api.get(`/api/rx/${pharmacyId}/rx-detail/${rxNumber}`);
      if (!response.data?.success) {
        throw new Error(response.data?.message || 'Failed to load fill history');
      }
      setFills(response.data.data?.rxDetails || []);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to load fill history');
      setFills(null);
    } finally {
      setLoading(false);
    }
  }, [rxNumber, pharmacyId]);

  useEffect(() => {
    if (open) fetchFillHistory();
  }, [open, fetchFillHistory]);

  const summary = summarizeFillHistory(fills);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box component="span" sx={{ fontWeight: 600 }}>
          Fill History — Rx #{rxNumber ?? '—'}
          {medicationName ? ` · ${medicationName}` : ''}
        </Box>
        <IconButton onClick={onClose} size="small" aria-label="Close fill history">
          <CloseIcon aria-hidden="true" />
        </IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert
            severity="warning"
            action={
              <Button color="inherit" size="small" startIcon={<RefreshIcon />} onClick={fetchFillHistory}>
                Retry
              </Button>
            }
          >
            {error}
          </Alert>
        ) : (
          <>
            <Stack
              direction="row"
              spacing={1}
              flexWrap="wrap"
              sx={{ gap: 1, mb: 2 }}
              data-testid="rx-fill-history-summary"
            >
              <Chip label={`${summary.total} fill${summary.total === 1 ? '' : 's'}`} color="primary" size="small" />
              {summary.cancelled > 0 && (
                <Chip label={`${summary.cancelled} cancelled`} color="error" size="small" variant="outlined" />
              )}
              {summary.firstFillDate && (
                <Chip label={`First: ${formatDateForDisplay(summary.firstFillDate)}`} size="small" variant="outlined" />
              )}
              {summary.lastFillDate && (
                <Chip label={`Last: ${formatDateForDisplay(summary.lastFillDate)}`} size="small" variant="outlined" />
              )}
            </Stack>
            <RxFillHistoryTable fills={fills || []} highlightRxNum={rxNumber} />
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default RxFillHistoryDialog;
